// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Button, { ButtonProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectProps } from "@insite/mobius/Select";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import TagWrapper, { TagWrapperProps } from "@store/Library/Components/Toolkit/TagWrapper";
import { tableFilterProps } from "@store/Themes/common/props/table";
import constants from "@store/constants";
import { GetLedgersApiParameter } from "@store/Framework/Services/LedgerService";
import { useLedgerDocumentTypes, useLedgerStatuses } from "@store/Framework/Store/Store/Data/Ledgers/LedgersHooks";
import {
    useBillingStatusFiltersOpen,
    useLedgersParameter,
} from "@store/Framework/Store/Store/Pages/BillingStatus/BillingStatusHooks";
import clearSearch from "@store/Framework/Store/Store/Pages/BillingStatus/Handlers/ClearSearch";
import toggleFiltersOpen from "@store/Framework/Store/Store/Pages/BillingStatus/Handlers/ToggleFiltersOpen";
import updateSearchFields from "@store/Framework/Store/Store/Pages/BillingStatus/Handlers/UpdateSearchFields";
import { useCurrentLanguage } from "@toolkit/Framework/Store/Context/ContextHooks";
import * as React from "react";
import { useState } from "react";

const gridItemProps: GridItemProps = { width: [12, 6, 6, 3, 3] };

const enum fields {
    showInvoiceDate = "showInvoiceDate",
    showDueDate = "showDueDate",
    showDocumentType = "showDocumentType",
    showStatus = "showStatus",
    showPoNumber = "showPoNumber",
    showInvoiceNumber = "showInvoiceNumber",
}

interface BillingStatusFilterProps extends WidgetProps {
    fields: {
        [fields.showInvoiceDate]: boolean;
        [fields.showDueDate]: boolean;
        [fields.showDocumentType]: boolean;
        [fields.showStatus]: boolean;
        [fields.showPoNumber]: boolean;
        [fields.showInvoiceNumber]: boolean;
    };
}

export interface BillingStatusFilterStyles {
    container?: GridContainerProps;
    wrapper?: InjectableCss;
    datePicker?: DatePickerPresentationProps;
    clearFiltersButton?: ButtonProps;
    accordion?: AccordionPresentationProps;
    tagWrapper?: TagWrapperProps;
    appliedFilterTag?: TagPresentationProps;
    select?: SelectProps;
    filterGridItem?: GridItemProps;
}

export const filterStyles: BillingStatusFilterStyles = {
    wrapper: tableFilterProps.wrapper,
    accordion: tableFilterProps.accordion,
    container: tableFilterProps.formGridContainer,
    clearFiltersButton: tableFilterProps.clearButton,
    tagWrapper: tableFilterProps.tagWrapper,
    filterGridItem: gridItemProps,
};

const styles = filterStyles;

const BillingStatusFilter = ({
    fields: { showInvoiceDate, showDueDate, showDocumentType, showStatus, showPoNumber, showInvoiceNumber },
}: BillingStatusFilterProps) => {
    const language = useCurrentLanguage();
    const dispatch = useAppDispatch();
    const ledgersParameter = useLedgersParameter();
    const billingStatusFiltersOpen = useBillingStatusFiltersOpen();
    const ledgerDocumentTypes = useLedgerDocumentTypes();
    const ledgerStatuses = useLedgerStatuses();

    const [documentType, setDocumentType] = useState(ledgersParameter.documentType || "");
    const [status, setStatus] = useState(ledgersParameter.status || "");
    const [poNumber, setPoNumber] = useState(ledgersParameter.poNumber || "");
    const [invoiceNumber, setInvoiceNumber] = useState(ledgersParameter.invoiceNumber || "");
    const [updateTimeoutId, setUpdateTimeoutId] = useState<NodeJS.Timeout | undefined>(undefined);

    const updateParameterAfterTimeout = (payload: Partial<GetLedgersApiParameter>) => {
        clearTimeout(updateTimeoutId);

        setUpdateTimeoutId(
            setTimeout(() => {
                dispatch(updateSearchFields(payload));
            }, 250),
        );
    };

    const onTogglePanelHandler = () => {
        dispatch(toggleFiltersOpen());
    };

    const invoiceDateOnChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        dispatch(
            updateSearchFields({
                invoiceDate: convertDateToDateOnlyString(selectedDay),
            }),
        );
    };

    const dueDateOnChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        dispatch(
            updateSearchFields({
                dueDate: convertDateToDateOnlyString(selectedDay),
            }),
        );
    };

    const clearFiltersClickHandler = () => {
        setDocumentType("");
        setStatus("");
        setPoNumber("");
        setInvoiceNumber("");
        dispatch(clearSearch());
    };

    const documentTypeChangeHandler = (value: string) => {
        setDocumentType(value);
        dispatch(
            updateSearchFields({
                documentType: value,
            }),
        );
    };

    const statusChangeHandler = (value: string) => {
        setStatus(value);
        dispatch(
            updateSearchFields({
                status: value,
            }),
        );
    };

    const poNumberChangeHandler = (value: string, debounce: boolean) => {
        setPoNumber(value);

        if (debounce) {
            updateParameterAfterTimeout({ poNumber: value });
        } else {
            dispatch(
                updateSearchFields({
                    poNumber: value,
                }),
            );
        }
    };

    const invoiceNumberChangeHandler = (value: string, debounce: boolean) => {
        setInvoiceNumber(value);

        if (debounce) {
            updateParameterAfterTimeout({ invoiceNumber: value });
        } else {
            dispatch(
                updateSearchFields({
                    invoiceNumber: value,
                }),
            );
        }
    };

    const invoiceDate = ledgersParameter.invoiceDate
        ? new Date(ledgersParameter.invoiceDate.replace(/-/g, "/"))
        : undefined;

    const dueDate = ledgersParameter.dueDate ? new Date(ledgersParameter.dueDate.replace(/-/g, "/")) : undefined;

    const facets =
        ledgersParameter.invoiceDate ||
        ledgersParameter.dueDate ||
        ledgersParameter.documentType ||
        ledgersParameter.status ||
        ledgersParameter.poNumber ||
        ledgersParameter.invoiceNumber ? (
            <TagWrapper {...styles.tagWrapper}>
                {ledgersParameter.invoiceDate && (
                    <Tag {...styles.appliedFilterTag} onDelete={() => invoiceDateOnChangeHandler({})}>
                        {`${translate("{0}:", translate("Invoice Date"))} ${getLocalizedDateTime({
                            dateTime: convertDateOnlyStringToDate(ledgersParameter.invoiceDate) as Date,
                            language,
                        })}`}
                    </Tag>
                )}
                {ledgersParameter.dueDate && (
                    <Tag {...styles.appliedFilterTag} onDelete={() => dueDateOnChangeHandler({})}>
                        {`${translate("{0}:", translate("Due Date"))} ${getLocalizedDateTime({
                            dateTime: convertDateOnlyStringToDate(ledgersParameter.dueDate) as Date,
                            language,
                        })}`}
                    </Tag>
                )}
                {ledgersParameter.documentType && (
                    <Tag {...styles.appliedFilterTag} onDelete={() => documentTypeChangeHandler("")}>
                        {`${translate("{0}:", translate("Document Type"))} ${siteMessage(`${constants.ledger.documentType}_${ledgersParameter.documentType}`)}`}
                    </Tag>
                )}
                {ledgersParameter.status && (
                    <Tag {...styles.appliedFilterTag} onDelete={() => statusChangeHandler("")}>
                        {`${translate("{0}:", translate("Status"))} ${siteMessage(`${constants.ledger.status}_${ledgersParameter.status}`)}`}
                    </Tag>
                )}
                {ledgersParameter.poNumber && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            poNumberChangeHandler("", false);
                        }}
                    >
                        {`${translate("{0}:", translate("PO #"))} ${ledgersParameter.poNumber}`}
                    </Tag>
                )}
                {ledgersParameter.invoiceNumber && (
                    <Tag
                        {...styles.appliedFilterTag}
                        onDelete={() => {
                            invoiceNumberChangeHandler("", false);
                        }}
                    >
                        {`${translate("{0}:", translate("Invoice #"))} ${ledgersParameter.invoiceNumber}`}
                    </Tag>
                )}
            </TagWrapper>
        ) : null;

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="billingStatus_filter">
            <Accordion {...styles.accordion} headingLevel={2}>
                <AccordionSection
                    data-test-selector="billingStatus_filterHeader"
                    title={translate("Filters")}
                    expanded={billingStatusFiltersOpen}
                    onTogglePanel={onTogglePanelHandler}
                >
                    <GridContainer {...styles.container}>
                        {showInvoiceDate !== false && (
                            <GridItem {...styles.filterGridItem} data-test-selector="billingStatus_invoiceDate">
                                <DatePicker
                                    {...styles.datePicker}
                                    label={translate("Invoice Date")}
                                    selectedDay={invoiceDate}
                                    onDayChange={invoiceDateOnChangeHandler}
                                    dateTimePickerProps={{
                                        clearIcon: null,
                                        ...styles.datePicker?.dateTimePickerProps,
                                    }}
                                />
                            </GridItem>
                        )}
                        {showDueDate !== false && (
                            <GridItem {...styles.filterGridItem} data-test-selector="billingStatus_dueDate">
                                <DatePicker
                                    {...styles.datePicker}
                                    label={translate("Due Date")}
                                    selectedDay={dueDate}
                                    onDayChange={dueDateOnChangeHandler}
                                    dateTimePickerProps={{
                                        clearIcon: null,
                                        ...styles.datePicker?.dateTimePickerProps,
                                    }}
                                />
                            </GridItem>
                        )}
                        {showDocumentType !== false && (
                            <GridItem {...styles.filterGridItem} data-test-selector="billingStatus_documentType">
                                <Select
                                    label={translate("Document Type")}
                                    {...styles.select}
                                    value={documentType}
                                    onChange={event => documentTypeChangeHandler(event.target.value)}
                                >
                                    <option value="">{translate("Select")}</option>
                                    {ledgerDocumentTypes.map(documentType => (
                                        <option key={documentType} value={documentType}>
                                            {siteMessage(`${constants.ledger.documentType}_${documentType}`)}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        {showStatus !== false && (
                            <GridItem {...styles.filterGridItem} data-test-selector="billingStatus_status">
                                <Select
                                    label={translate("Status")}
                                    {...styles.select}
                                    value={status}
                                    onChange={event => statusChangeHandler(event.target.value)}
                                >
                                    <option value="">{translate("Select")}</option>
                                    {ledgerStatuses.map(status => (
                                        <option key={status} value={status}>
                                            {siteMessage(`${constants.ledger.status}_${status}`)}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                        )}
                        {showPoNumber !== false && (
                            <GridItem {...styles.filterGridItem} data-test-selector="billingStatus_poNumber">
                                <TextField
                                    value={poNumber || ""}
                                    onChange={event => poNumberChangeHandler(event.target.value, true)}
                                    label={translate("PO #")}
                                />
                            </GridItem>
                        )}
                        {showInvoiceNumber !== false && (
                            <GridItem {...styles.filterGridItem} data-test-selector="billingStatus_invoiceNumber">
                                <TextField
                                    value={invoiceNumber || ""}
                                    onChange={event => invoiceNumberChangeHandler(event.target.value, true)}
                                    label={translate("Invoice #")}
                                />
                            </GridItem>
                        )}
                    </GridContainer>
                    <FormActions>
                        <Button
                            {...styles.clearFiltersButton}
                            onClick={clearFiltersClickHandler}
                            data-test-selector="billingStatus_clearFiltersButton"
                        >
                            {translate("Clear")}
                            <VisuallyHidden>{translate("All Filters")}</VisuallyHidden>
                        </Button>
                    </FormActions>
                </AccordionSection>
            </Accordion>
            {facets}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BillingStatusFilter,
    definition: {
        group: constants.widgetGroup.billingStatus,
        displayName: "Billing Status Filter",
        allowedContexts: [constants.pageType.billingStatus],
        fieldDefinitions: [
            {
                name: fields.showInvoiceDate,
                displayName: "Invoice Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showDueDate,
                displayName: "Due Date",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showDocumentType,
                displayName: "Document Type",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPoNumber,
                displayName: "PO Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showInvoiceNumber,
                displayName: "Invoice Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
        ],
    },
};

export default widgetModule;
